import type { PartDefinition } from "@rogue/rackbuilder"
import { getWeightStackOffset, getFrontCableOffset, getBackCableOffset, getHRShroudOffset } from "./indyOffsets"
import { is80Height, getFlipX, isHalfRack, is100Height, is24Depth, is90Height, is30Depth, is43Depth, IndyContext } from "./indyHelpers"
import { getRackModel } from "@/js/pages/product/rackbuilder/modelPaths"
import { buildTopWeightStackHardware, buildWeightStack300Plates } from "../ftSharedParts"

export { buildTopWeightStackHardware, buildWeightStack300Plates }

export function buildWeightStackPoles(context: IndyContext): PartDefinition {
  const { config, materials } = context
  
  return {
    mesh: is80Height(config) 
      ? getRackModel('INDY_80', 'stack poles 80')
      : getRackModel('INDY', `stack poles ${config.height}`),
    slots  : {},
    anchor : 'top',
    materials,
    offset : getWeightStackOffset(config),
  }
}

export function buildWeights(context: IndyContext): PartDefinition {
  const { config, materials, left } = context
  const topWeightStackHardware = buildTopWeightStackHardware()
  
  const weights: PartDefinition = {
    mesh        : getRackModel('FM_SHARED', 'weight stack'),
    materials,
    adjustments : getFlipX(left),
    offset      : getWeightStackOffset(config),
    slots       : {},
  }
  
  // Add weight stack hardware based on amount
  if (config.weightStackAmount === '300') {
    const weightStack300Plates = buildWeightStack300Plates()
    weights.slots.weights_300 = [topWeightStackHardware, weightStack300Plates]
  } else {
    weights.slots['weights_250'] = topWeightStackHardware
  }
  
  // Full rack includes back cable
  if (!isHalfRack(config)) {
    weights.slots.cable = buildBackCable(context)
  }
  
  return weights
}

// MARK: Cables
export function buildBottomWeightStackCable(context: IndyContext): PartDefinition {
  const { config } = context
  
  return {
    mesh: is100Height(config)
      ? getRackModel('INDY_CABLES', `weight stack 100 pulley cable ${config.weightStackAmount}`)
      : getRackModel('INDY_CABLES', `weight stack pulley cable ${config.weightStackAmount}`),
    slots: {},
  }
}

export function buildBottomWeightStackHRCable(context: IndyContext): PartDefinition {
  const { config } = context
  
  return {
    mesh  : getRackModel('INDY_CABLES', `weight stack hr pulley cable ${config.weightStackAmount}`),
    slots : {},
  }
}

export function buildFrontCable(context: IndyContext): PartDefinition {
  const { config, materials } = context
  
  // Full rack: simple cable without bottom attachment
  if (!isHalfRack(config)) {
    return {
      mesh: is80Height(config)
        ? getRackModel('INDY_CABLES', 'indy front cable 80')
        : getRackModel('INDY_CABLES', `indy front cable ${config.height}`),
      slots  : {},
      materials,
      offset : getFrontCableOffset(config),
    }
  }
  
  // Half rack: cable with bottom weight stack cable attachment
  return buildHalfRackFrontCable(context)
}

function buildHalfRackFrontCable(context: IndyContext): PartDefinition {
  const { config, materials } = context
  const bottomCable = buildBottomWeightStackHRCable(context)
  
  // 24" depth
  if (is24Depth(config)) {
    if (is80Height(config)) {
      return {
        mesh   : getRackModel('INDY_CABLES', 'indy hr front cable 24 80h'),
        slots  : { bottom: bottomCable },
        materials,
        offset : { z: -0.1 },
      }
    }
    
    return {
      mesh: is90Height(config)
        ? getRackModel('INDY_CABLES', 'indy hr front cable 24 90')
        : getRackModel('INDY_CABLES', 'indy hr front cable 24 100h'),
      slots  : { bottom: bottomCable },
      materials,
      offset : is90Height(config) ? { y: 0, z: -0.5 } : { y: 0, z: -0.7 },
    }
  }
  
  // 30" depth
  if (is30Depth(config)) {
    bottomCable.offset = { z: 0.025 }
    
    return {
      mesh: is80Height(config)
        ? getRackModel('INDY_CABLES', 'indy front cable 80')
        : is90Height(config)
          ? getRackModel('INDY_CABLES', 'indy hr front cable 30 90')
          : getRackModel('INDY_CABLES', 'indy hr front cable 30 100h'),
      slots  : { bottom: bottomCable },
      materials,
      offset : is100Height(config) ? { x: 0.1, z: -0.3 } : {},
    }
  }
  
  // 43" depth
  if (is43Depth(config)) {
    return {
      mesh: is90Height(config)
        ? getRackModel('INDY_CABLES', 'indy hr front cable 43 90')
        : getRackModel('INDY_CABLES', 'indy hr front cable 43 100h'),
      slots  : { bottom: bottomCable },
      materials,
      offset : is90Height(config) 
        ? { x: 0.3, z: -0.2 } 
        : { x: 0.3, y: -0.1, z: -0.4 },
    }
  }
  
  // Default fallback
  return {
    mesh   : getRackModel('INDY_CABLES', `indy front cable ${config.height}`),
    slots  : { bottom: bottomCable },
    materials,
    offset : getFrontCableOffset(config),
  }
}

export function buildBackCable(context: IndyContext): PartDefinition {
  const { config, materials, left } = context
  
  return {
    mesh: is80Height(config)
      ? getRackModel('INDY_CABLES', 'indy back cable 80')
      : getRackModel('INDY_CABLES', `indy back cable ${config.height}`),
    slots  : { bottom: buildBottomWeightStackCable(context) },
    materials,
    offset : getBackCableOffset(config, left),
  }
}

// MARK: Shrouds
export function buildShrouds(context: IndyContext): PartDefinition {
  const { config, materials } = context
  
  if (is80Height(config)) {
    return {
      mesh   : getRackModel('INDY_80', 'shroud 80'),
      slots  : {},
      materials,
      offset : getHRShroudOffset(config),
    }
  }
  
  // Non-80" shrouds come as left/right pair
  const rightShroud: PartDefinition = {
    mesh   : getRackModel('INDY', `shroud right ${config.height}h`),
    slots  : {},
    materials,
    offset : isHalfRack(config) && is30Depth(config) ? { z: 1.5 } : {},
  }
  
  return {
    mesh   : getRackModel('INDY', `shroud left ${config.height}h`),
    slots  : { right: rightShroud },
    materials,
    offset : isHalfRack(config) ? getHRShroudOffset(config) : { z: 0.3 },
  }
}

// MARK: Side Meshes Assembly
export function buildSideMeshes(context: IndyContext): PartDefinition[] {
  const { config } = context
  const sideMeshes: PartDefinition[] = []
  
  sideMeshes.push(buildWeightStackPoles(context))
  sideMeshes.push(buildWeights(context))
  
  if (config.ftShrouds) {
    sideMeshes.push(buildShrouds(context))
  }
  
  return sideMeshes
}